import React from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Button,
  alpha,
  useTheme,
  TextField,
  InputAdornment,
  Chip
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import BugReportIcon from '@mui/icons-material/BugReport';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useAuth } from '../../../context/AuthContext';

const OperationFilterPanel = ({
  // Filter options data
  defectTypes = [],
  devices = [],
  networks = [],
  sections = [],
  regions = [],
  // Currently selected filter values
  selectedDefectTypeId = '',
  selectedDeviceId = '',
  selectedNetworkId = '',
  selectedSectionId = '',
  selectedRegion = '',
  selectedValidationStatus = '',
  selectedActionStatus = '', // New prop for Action Status
  selectedDateRange = 'today', // today, yesterday, last7days, last30days, custom
  customDateFrom = '',
  customDateTo = '',
  // Callback functions to update state in parent
  onDefectTypeChange,
  onDeviceChange,
  onNetworkChange,
  onSectionChange,
  onRegionChange,
  onValidationStatusChange,
  onActionStatusChange, // New callback for Action Status
  onDateRangeChange,
  onCustomDateFromChange,
  onCustomDateToChange,
  onResetFilters,
  // Counts for display
  displayedCount = 0,
  totalCount = null,
  validatedCount = 0,
  pendingCount = 0,
  rejectedCount = 0,
  // Search term props
  searchTerm = '',
  onSearchTermChange,
}) => {
  const theme = useTheme();
  const { user, isAuthenticated, isLoading } = useAuth();
  
  const isSystemAdmin = user?.role === 'admin' && user?.organizationId === null;
  const userOrganizationId = user?.organizationId;

  const handleFilterChange = (event, handler) => {
    if (handler) {
      handler(event.target.value);
    }
  };

  // Show loading state if authentication is still loading
  if (isLoading) {
    return (
      <Paper
        elevation={6}
        sx={{
          position: 'absolute',
          top: 70,
          left: 16,
          zIndex: 10,
          padding: 2,
          backgroundColor: alpha(theme.palette.background.paper, 0.95),
          borderRadius: 2.5,
          minWidth: '340px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100px'
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Loading authentication...
        </Typography>
      </Paper>
    );
  }

  // Validation status options
  const validationStatusOptions = [
    { value: 'draft', label: 'Draft Findings', icon: <WarningIcon fontSize="small" />, color: 'warning' },
    { value: 'validated', label: 'Validated Defects', icon: <CheckCircleIcon fontSize="small" />, color: 'success' },
    { value: 'rejected', label: 'Rejected Findings', icon: <CancelIcon fontSize="small" />, color: 'error' }
  ];

  // Action Status options (from contractor_work_order_status_enum)
  const actionStatusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'assigned', label: 'Assigned' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  // Date range options
  const dateRangeOptions = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last7days', label: 'Last 7 Days' },
    { value: 'last30days', label: 'Last 30 Days' },
    { value: 'custom', label: 'Custom Range' }
  ];

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'absolute',
        top: 70,
        left: 16,
        zIndex: 10,
        padding: 2,
        backgroundColor: alpha(theme.palette.background.paper, 0.95),
        backdropFilter: 'blur(10px)',
        borderRadius: 2.5,
        minWidth: '340px',
        width: 'fit-content',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.2,
        maxHeight: 'calc(100vh - 120px)',
        overflowY: 'auto',
        boxShadow: '0 10px 30px rgba(0,0,0,0.12), 0 4px 8px rgba(0,0,0,0.06)',
        animation: 'fadeIn 0.3s ease-in-out',
        '@keyframes fadeIn': {
          '0%': { opacity: 0, transform: 'translateY(-20px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' }
        }
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <BugReportIcon color="primary" sx={{ mr: 1, fontSize: '1.2rem' }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1rem', color: theme.palette.primary.main }}>
          Filter Defects & Findings
        </Typography>
      </Box>

      {/* Status Summary Chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Chip
          icon={<CheckCircleIcon />}
          label={`${validatedCount} Validated`}
          size="small"
          color="success"
          variant="outlined"
          sx={{ fontSize: '0.7rem' }}
        />
        <Chip
          icon={<WarningIcon />}
          label={`${pendingCount} Pending`}
          size="small"
          color="warning"
          variant="outlined"
          sx={{ fontSize: '0.7rem' }}
        />
        <Chip
          icon={<CancelIcon />}
          label={`${rejectedCount} Rejected`}
          size="small"
          color="error"
          variant="outlined"
          sx={{ fontSize: '0.7rem' }}
        />
      </Box>

      {/* Search Input */}
      <FormControl fullWidth variant="outlined" sx={{ mb: 1.5 }}>
        <TextField
          placeholder="Search Defects & Findings"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => onSearchTermChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" sx={{ fontSize: '1.2rem' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              fontSize: '0.9rem',
              backgroundColor: alpha(theme.palette.background.default, 0.6),
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: alpha(theme.palette.background.default, 0.8),
                boxShadow: '0 2px 6px rgba(0,0,0,0.05)'
              },
              '&.Mui-focused': {
                backgroundColor: alpha(theme.palette.background.paper, 0.95),
                boxShadow: '0 3px 8px rgba(0,0,0,0.08)'
              }
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(theme.palette.primary.main, 0.2),
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(theme.palette.primary.main, 0.5),
            },
          }}
        />
      </FormControl>

      {/* Date Range Filter */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 0.8,
        backgroundColor: alpha(theme.palette.background.default, 0.3),
        padding: 1.2,
        borderRadius: 2,
        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <Typography 
          variant="caption" 
          sx={{ 
            fontWeight: 600, 
            color: alpha(theme.palette.text.primary, 0.7),
            mb: 0.5,
            fontSize: '0.75rem',
            letterSpacing: '0.03em',
            textTransform: 'uppercase'
          }}
        >
          Time Range
        </Typography>

        <FormControl fullWidth size="small" variant="outlined" sx={{ mb: 0.5 }}>
          <InputLabel id="date-range-label" sx={{ fontSize: '0.85rem' }}>Date Range</InputLabel>
          <Select
            labelId="date-range-label"
            label="Date Range"
            value={selectedDateRange}
            onChange={(e) => handleFilterChange(e, onDateRangeChange)}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.text.primary, 0.15),
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              borderRadius: 1.5,
              fontSize: '0.85rem'
            }}
          >
            {dateRangeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.85rem' }}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Custom Date Range Inputs */}
        {selectedDateRange === 'custom' && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="From Date"
              type="date"
              size="small"
              value={customDateFrom}
              onChange={(e) => onCustomDateFromChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1, fontSize: '0.85rem' }}
            />
            <TextField
              label="To Date"
              type="date"
              size="small"
              value={customDateTo}
              onChange={(e) => onCustomDateToChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1, fontSize: '0.85rem' }}
            />
          </Box>
        )}
      </Box>

      {/* Status and Priority Filters */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 0.8,
        backgroundColor: alpha(theme.palette.background.default, 0.3),
        padding: 1.2,
        borderRadius: 2,
        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)',
        mt: 0.5
      }}>
        <Typography 
          variant="caption" 
          sx={{ 
            fontWeight: 600, 
            color: alpha(theme.palette.text.primary, 0.7),
            mb: 0.5,
            fontSize: '0.75rem',
            letterSpacing: '0.03em',
            textTransform: 'uppercase'
          }}
        >
          STATUS
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* Finding Status Filter */}
          <FormControl size="small" variant="outlined" sx={{ mb: 0.5, flex: 1 }}>
            <InputLabel id="validation-status-label" sx={{ fontSize: '0.85rem' }}>Finding Status</InputLabel>
            <Select
              labelId="validation-status-label"
              label="Finding Status"
              value={selectedValidationStatus}
              onChange={(e) => handleFilterChange(e, onValidationStatusChange)}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.text.primary, 0.15),
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                },
                borderRadius: 1.5,
                fontSize: '0.85rem'
              }}
            >
              <MenuItem value="" sx={{ fontSize: '0.85rem' }}>
                <em>All Statuses</em>
              </MenuItem>
              {validationStatusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.85rem' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {option.icon}
                    {option.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Action Status Filter */}
          <FormControl size="small" variant="outlined" sx={{ mb: 0.5, flex: 1 }}>
            <InputLabel id="action-status-label" sx={{ fontSize: '0.85rem' }}>Action Status</InputLabel>
            <Select
              labelId="action-status-label"
              label="Action Status"
              value={selectedActionStatus}
              onChange={(e) => handleFilterChange(e, onActionStatusChange)}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.text.primary, 0.15),
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                },
                borderRadius: 1.5,
                fontSize: '0.85rem'
              }}
            >
              <MenuItem value="" sx={{ fontSize: '0.85rem' }}>
                <em>All Statuses</em>
              </MenuItem>
              {actionStatusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.85rem' }}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Defect Type and Device Filters */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 0.8,
        backgroundColor: alpha(theme.palette.background.default, 0.3),
        padding: 1.2,
        borderRadius: 2,
        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)',
        mt: 0.5
      }}>
        <Typography 
          variant="caption" 
          sx={{ 
            fontWeight: 600, 
            color: alpha(theme.palette.text.primary, 0.7),
            mb: 0.5,
            fontSize: '0.75rem',
            letterSpacing: '0.03em',
            textTransform: 'uppercase'
          }}
        >
          Defect Type & Device
        </Typography>

        {/* Defect Type Filter */}
        <FormControl fullWidth size="small" variant="outlined" sx={{ mb: 0.5 }}>
          <InputLabel id="defect-type-label" sx={{ fontSize: '0.85rem' }}>Defect Type</InputLabel>
          <Select
            labelId="defect-type-label"
            label="Defect Type"
            value={selectedDefectTypeId}
            onChange={(e) => handleFilterChange(e, onDefectTypeChange)}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.text.primary, 0.15),
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              borderRadius: 1.5,
              fontSize: '0.85rem'
            }}
          >
            <MenuItem value="" sx={{ fontSize: '0.85rem' }}>
              <em>All Defect Types</em>
            </MenuItem>
            {defectTypes.map((type) => (
              <MenuItem key={type.id} value={type.id} sx={{ fontSize: '0.85rem' }}>
                {type.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Device Filter */}
        <FormControl fullWidth size="small" variant="outlined" sx={{ mb: 0.3 }}>
          <InputLabel id="device-label" sx={{ fontSize: '0.85rem' }}>Device</InputLabel>
          <Select
            labelId="device-label"
            label="Device"
            value={selectedDeviceId}
            onChange={(e) => handleFilterChange(e, onDeviceChange)}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.text.primary, 0.15),
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              borderRadius: 1.5,
              fontSize: '0.85rem'
            }}
          >
            <MenuItem value="" sx={{ fontSize: '0.85rem' }}>
              <em>All Devices</em>
            </MenuItem>
            {devices.map((device) => (
              <MenuItem key={device.id} value={device.id} sx={{ fontSize: '0.85rem' }}>
                {device.name || device.device_name || device.uuid}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Location Filters */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 0.8,
        backgroundColor: alpha(theme.palette.background.default, 0.3),
        padding: 1.2,
        borderRadius: 2,
        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)',
        mt: 0.5
      }}>
        <Typography 
          variant="caption" 
          sx={{ 
            fontWeight: 600, 
            color: alpha(theme.palette.text.primary, 0.7),
            mb: 0.5,
            fontSize: '0.75rem',
            letterSpacing: '0.03em',
            textTransform: 'uppercase'
          }}
        >
          Location Filters
        </Typography>

        {/* Network and Region Filters - Side by Side */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FormControl size="small" variant="outlined" sx={{ mb: 0.5, flex: 1 }}>
            <InputLabel id="network-filter-label" sx={{ fontSize: '0.85rem' }}>Network</InputLabel>
            <Select
              labelId="network-filter-label"
              label="Network"
              value={selectedNetworkId}
              onChange={(e) => handleFilterChange(e, onNetworkChange)}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.text.primary, 0.15),
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                },
                borderRadius: 1.5,
                fontSize: '0.85rem'
              }}
            >
              <MenuItem value="" sx={{ fontSize: '0.85rem' }}>
                <em>All Networks</em>
              </MenuItem>
              {networks.map((net) => (
                <MenuItem key={net.id} value={net.id} sx={{ fontSize: '0.85rem' }}>
                  {net.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" variant="outlined" sx={{ mb: 0.5, flex: 1 }}>
            <InputLabel id="region-filter-label" sx={{ fontSize: '0.85rem' }}>Region</InputLabel>
            <Select
              labelId="region-filter-label"
              label="Region"
              value={selectedRegion}
              onChange={(e) => handleFilterChange(e, onRegionChange)}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.text.primary, 0.15),
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                },
                borderRadius: 1.5,
                fontSize: '0.85rem'
              }}
            >
              <MenuItem value="" sx={{ fontSize: '0.85rem' }}>
                <em>All Regions</em>
              </MenuItem>
              {regions.map((region) => (
                <MenuItem key={region} value={region} sx={{ fontSize: '0.85rem' }}>
                  {region}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Section Filter */}
        <FormControl fullWidth size="small" variant="outlined" sx={{ mb: 0.3 }}>
          <InputLabel id="section-filter-label" sx={{ fontSize: '0.85rem' }}>Section</InputLabel>
          <Select
            labelId="section-filter-label"
            label="Section"
            value={selectedSectionId}
            onChange={(e) => handleFilterChange(e, onSectionChange)}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.text.primary, 0.15),
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: alpha(theme.palette.primary.main, 0.5),
              },
              borderRadius: 1.5,
              fontSize: '0.85rem'
            }}
          >
            <MenuItem value="" sx={{ fontSize: '0.85rem' }}>
              <em>All Sections</em>
            </MenuItem>
            {sections.map((sec) => (
              <MenuItem key={sec.id} value={sec.id} sx={{ fontSize: '0.85rem' }}>
                {sec.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Reset Button */}
      <Button
        variant="outlined"
        startIcon={<RestartAltIcon sx={{ fontSize: '1rem' }} />}
        onClick={onResetFilters}
        sx={{ 
          mt: 1.5,
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.85rem',
          py: 0.7,
          color: theme.palette.primary.main,
          borderColor: alpha(theme.palette.primary.main, 0.5),
          boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
          transition: 'all 0.2s',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            borderColor: theme.palette.primary.main,
            boxShadow: '0 4px 6px rgba(0,0,0,0.08)',
            transform: 'translateY(-1px)'
          }
        }}
      >
        Reset Filters
      </Button>

      {/* Display Count Status */}
      <Box 
        sx={{ 
          mt: 1.5,
          py: 0.9, 
          px: 1.5, 
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          borderRadius: 2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.03)'
        }}
      >
        <Typography 
          variant="body2" 
          sx={{ 
            color: theme.palette.primary.main,
            fontWeight: 500,
            fontSize: '0.8rem'
          }}
        >
          {totalCount !== null
            ? `Displaying ${displayedCount} of ${totalCount} defects/findings`
            : `Displaying ${displayedCount} defects/findings`}
        </Typography>
      </Box>
    </Paper>
  );
};

export default OperationFilterPanel; 